import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import Form from "@/components/Form";
import Field from "@/components/Field";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/NumberInput";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { costItemSchema } from "@/schemas/costs";
import {
  DialogClose,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import SaveButton from "@/containers/survey/components/Toolbar/components/SaveButton";
import { CostFormData } from "@/types/costs";

const DEFAULT_FORM_VALUES: CostFormData = {
  name: "",
  amount: "",
  comment: "",
};

interface AddCostsDialogFormProps {
  isSubmitting: boolean;
  onSubmit: (data: CostFormData) => void;
}

export const AddCostsDialogForm = ({
  onSubmit,
  isSubmitting,
}: AddCostsDialogFormProps) => {
  const { t } = useTranslation();

  const form = useForm<CostFormData>({
    resolver: zodResolver(costItemSchema),
    defaultValues: DEFAULT_FORM_VALUES,
    disabled: isSubmitting,
  });

  const handleSubmit = async (data: CostFormData) => {
    onSubmit(data);
  };

  return (
    <Form form={form} handleSubmit={handleSubmit}>
      <DialogHeader>
        <DialogTitle>{t("costs.dialog.title")}</DialogTitle>
      </DialogHeader>
      <div className="grid grid-cols-1 gap-4">
        <Field
          name="name"
          label={t("costs.fields.name")}
          required
          render={({ field }) => <Input {...field} />}
        />
        <Field
          name="amount"
          label={t("costs.fields.amount")}
          required
          render={({ field }) => <NumberInput {...field} />}
        />
        <Field
          name="comment"
          label={t("costs.fields.comment")}
          render={({ field }) => <Textarea {...field} rows={1} />}
        />
      </div>
      <DialogFooter className="gap-3 ">
        <DialogClose asChild>
          <Button variant="outline">{t("common.cancel")}</Button>
        </DialogClose>
        <SaveButton />
      </DialogFooter>
    </Form>
  );
};
